import { useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import {
  hexToRgb255,
  isHexColorString,
  rgb255ToHex,
  type Rgb255,
} from "../color/rgbCodec";
import { useT } from "../i18n";

/**
 * AutoCAD "Select Color" → **True Color** tab (RGB model).
 *
 * Mirrors AutoCAD's True Color tab: three 0–255 channel spin-fields
 * (Red / Green / Blue) plus a `#rrggbb` hex "Color" field, all kept in sync,
 * a live "True Color" preview swatch, and an OK affordance that commits the
 * selection. (AutoCAD's HSL model toggle is out of S3a scope — RGB only.)
 *
 * The committed value is a lowercase `#rrggbb` string — the canonical form the
 * property apply path already accepts (`deserializeColor` → `coerceColor` →
 * `{ kind: "rgb" }`). The Color Books tab is DEFERRED (matches the S1/S2
 * bookColor-no-palette deferral); no fake palette is stubbed here.
 */

type TrueColorTabProps = {
  /**
   * Initial hex (`#rrggbb` or bare `rrggbb`) to seed the channels from when the
   * cell's current colour is already a true colour. Falsy / non-hex → black.
   */
  initialHex: string | null;
  /**
   * Commit a true-colour selection as a lowercase `#rrggbb` string — wired
   * straight through to the picker's `onSelect`, which the property apply path
   * accepts verbatim.
   */
  onSelect: (hex: string) => void;
};

function clampChannel(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(255, Math.round(n)));
}

export function TrueColorTab({ initialHex, onSelect }: TrueColorTabProps) {
  const t = useT();
  const seed: Rgb255 =
    initialHex && isHexColorString(initialHex)
      ? hexToRgb255(initialHex)
      : { r: 0, g: 0, b: 0 };

  const [rgb, setRgb] = useState<Rgb255>(seed);
  // Hex input is kept as its own editable string so a partially-typed value
  // doesn't get clobbered mid-edit; channels re-derive only on a valid 6-hex.
  const [hexInput, setHexInput] = useState<string>(rgb255ToHex(seed));

  const currentHex = rgb255ToHex(rgb);

  function setChannel(key: keyof Rgb255, raw: string) {
    const next = { ...rgb, [key]: clampChannel(Number(raw)) };
    setRgb(next);
    setHexInput(rgb255ToHex(next));
  }

  function onHexChange(raw: string) {
    setHexInput(raw);
    if (isHexColorString(raw)) {
      setRgb(hexToRgb255(raw));
    }
  }

  // The picker renders inside InspectorSidebar's properties <form>, so a raw
  // Enter in any of these fields would submit that form (applying the current
  // propertyDraft) BEFORE the picker commits — losing the typed colour (@codex
  // P2). Intercept Enter: stop the form submit and commit the current colour
  // exactly like the OK button.
  function onFieldKeyDown(e: ReactKeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      onSelect(currentHex);
    }
  }

  return (
    <div className="true-color-tab" data-testid="true-color-tab">
      <div className="true-color-channels">
        <label className="true-color-channel">
          <span>{t("color_picker.true.r")}</span>
          <input
            type="number"
            min={0}
            max={255}
            value={rgb.r}
            data-testid="true-color-r"
            onKeyDown={onFieldKeyDown}
            onChange={(e) => setChannel("r", e.target.value)}
          />
        </label>
        <label className="true-color-channel">
          <span>{t("color_picker.true.g")}</span>
          <input
            type="number"
            min={0}
            max={255}
            value={rgb.g}
            data-testid="true-color-g"
            onKeyDown={onFieldKeyDown}
            onChange={(e) => setChannel("g", e.target.value)}
          />
        </label>
        <label className="true-color-channel">
          <span>{t("color_picker.true.b")}</span>
          <input
            type="number"
            min={0}
            max={255}
            value={rgb.b}
            data-testid="true-color-b"
            onKeyDown={onFieldKeyDown}
            onChange={(e) => setChannel("b", e.target.value)}
          />
        </label>
      </div>

      <label className="true-color-hex">
        <span>{t("color_picker.true.hex")}</span>
        <input
          type="text"
          value={hexInput}
          data-testid="true-color-hex"
            onKeyDown={onFieldKeyDown}
          onChange={(e) => onHexChange(e.target.value)}
        />
      </label>

      <div className="true-color-preview-row">
        <span className="true-color-preview-label">
          {t("color_picker.true.preview")}
        </span>
        <span
          className="true-color-preview"
          data-testid="true-color-preview"
          style={{ backgroundColor: currentHex }}
        />
      </div>

      <button
        type="button"
        className="true-color-ok"
        data-testid="true-color-ok"
        onClick={() => onSelect(currentHex)}
      >
        {t("color_picker.ok")}
      </button>
    </div>
  );
}
